import { isEmpty } from './field'
import type { PortalConfig } from './config'
import type { SectionDocs } from './sections'

/**
 * Everything in a client's onboarding that hangs off one brand. Removing a brand removes all of it,
 * so the confirm dialog (to show it) and the removeBrand callable (to delete it) share this one map.
 */
export type BrandFootprint = {
  locations: Array<{ id: string; name: string }>
  /** audience.items.{brandId} exists; `audienceAnswered` says whether anything was filled in. */
  audience: boolean
  audienceAnswered: boolean
  /** social.items keys for this brand, and those with any answer or saved login. */
  accounts: string[]
  accountsAnswered: number
  secretIds: string[]
  keyLinks: boolean
  keyLinksAnswered: boolean
  brandKit: boolean
  brandKitAnswered: boolean
  /** faq.brands.{brandId} exists. */
  faq: boolean
  faqAnswered: boolean
  uploads: Array<{ id: string; name: string; storagePath: string }>
  platformOverrides: boolean
}

/** True when any field envelope under `node` holds a value. */
function hasAnswers(node: unknown): boolean {
  if (node == null || typeof node !== 'object') return false
  if ('value' in node && 'updatedAt' in node) return !isEmpty((node as { value: unknown }).value)
  return Object.values(node).some(hasAnswers)
}

export function brandFootprint(docs: Partial<SectionDocs>, config: PortalConfig | null, brandId: string): BrandFootprint {
  const locations = Object.entries(docs.locations?.items ?? {})
    .filter(([, l]) => l.brandId === brandId)
    .map(([id, l]) => ({ id, name: l.name?.value || 'Untitled location' }))
  const accountEntries = Object.entries(docs.social?.items ?? {}).filter(([, a]) => a.brandId === brandId)
  const audience = docs.audience?.items?.[brandId]
  const keyLinks = docs.links?.keyLinks?.[brandId]
  const brandKit = docs.assets?.brandKit?.[brandId]
  const faq = docs.faq?.brands?.[brandId]
  return {
    locations,
    audience: audience !== undefined,
    audienceAnswered: hasAnswers(audience),
    accounts: accountEntries.map(([id]) => id),
    accountsAnswered: accountEntries.filter(([, a]) => a.secretRef || hasAnswers(a)).length,
    secretIds: accountEntries.map(([, a]) => a.secretRef).filter((s): s is string => Boolean(s)),
    keyLinks: keyLinks !== undefined,
    keyLinksAnswered: hasAnswers(keyLinks),
    brandKit: brandKit !== undefined,
    brandKitAnswered: hasAnswers(brandKit),
    faq: faq !== undefined,
    faqAnswered: hasAnswers(faq),
    uploads: Object.entries(docs.assets?.uploads ?? {})
      .filter(([, u]) => u.brandId === brandId)
      .map(([id, u]) => ({ id, name: u.name, storagePath: u.storagePath })),
    platformOverrides: config?.platformOverrides?.[brandId] !== undefined,
  }
}
